import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Table,
  TableSortLabel,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";

import { formatDate } from "../../../common/utils";
import { ESTEVES_LABELS } from "../../../common/metadata";

const getEstevesLabel = (lrr) => {
  if (lrr === undefined || lrr === null || isNaN(lrr)) return undefined;
  if (lrr < -1.0) return ESTEVES_LABELS["Critically Eroded"].class;
  if (lrr < -0.5) return ESTEVES_LABELS["Eroded"].class;
  if (lrr < 0.5) return ESTEVES_LABELS["Stable"].class;
  return ESTEVES_LABELS["Accreted"].class;
};

const column = (key, label, numeric = true, format = undefined) => ({
  key,
  label,
  numeric,
  format,
});

const COLUMNS = [
  column("id", "ID", true, (x) => parseInt(x, 10)),
  column(
    "latStart",
    "forms.shorelineAnalysis.transectsReport.headers.initialLatitude"
  ),
  column(
    "longStart",
    "forms.shorelineAnalysis.transectsReport.headers.initialLongitude"
  ),
  column(
    "latEnd",
    "forms.shorelineAnalysis.transectsReport.headers.finalLatitude"
  ),
  column(
    "longEnd",
    "forms.shorelineAnalysis.transectsReport.headers.finalLongitude"
  ),
  column(
    "firstDate",
    "forms.shorelineAnalysis.transectsReport.headers.initialDate",
    false,
    formatDate
  ),
  column(
    "lastDate",
    "forms.shorelineAnalysis.transectsReport.headers.finalDate",
    false,
    formatDate
  ),
  column(
    "intercept",
    "forms.shorelineAnalysis.transectsReport.headers.intercept"
  ),
  column("slope", "forms.shorelineAnalysis.transectsReport.headers.slope"),
  column(
    "rsquared",
    "forms.shorelineAnalysis.transectsReport.headers.rsquared"
  ),
  column("lrr", "forms.shorelineAnalysis.transectsReport.headers.lrr"),
  column("sce", "forms.shorelineAnalysis.transectsReport.headers.sce"),
  column("nsm", "forms.shorelineAnalysis.transectsReport.headers.nsm"),
  column("epr", "forms.shorelineAnalysis.transectsReport.headers.epr"),
  column(
    "label",
    "forms.shorelineAnalysis.transectsReport.headers.label",
    false
  ),
];

/**
 * @function TransectDataTable
 * @description React component that renders a sortable table of per-transect shoreline change
 * statistics (coordinates, dates, regression, LRR/SCE/NSM/EPR, classification label).
 * @param {Object} props - Component properties.
 * @param {Array<Object>} props.data - Transect statistics rows to display.
 * @see {@link sortedRows} - sorts the rows before rendering.
 * @see {@link handleSort} - updates the active sort column/direction.
 * **Called by:
 * @see {CoastlineEvolutionDialog} - as one of its report tabs.
 * @memberof module:SHARED
 * @returns {JSX.Element} The sortable transect statistics table.
 */
const TransectDataTable = ({ data }) => {
  const [t] = useTranslation();

  const [sortProperty, setSortProperty] = useState("id");
  const [ordering, setOrdering] = useState("asc");

  /**
   * @function sortedRows
   * @description Returns a copy of the data rows sorted by the currently selected column and direction.
   * **Called by:
   * @see {TransectDataTable} - to render the table body.
   * @memberof module:SHARED
   * @returns {Array<Object>} Sorted transect rows.
   */
  const sortedRows = () => {
    return [...data].sort((a, b) => {
      if (a[sortProperty] === b[sortProperty]) {
        return 0;
      }

      const val = a[sortProperty] < b[sortProperty] ? -1 : 1;

      if (ordering === "desc") {
        return -val;
      }

      return val;
    });
  };

  /**
   * @function handleSort
   * @description Updates the active sort column and direction: switches to ascending on a new
   * column, or toggles the direction when the same column is clicked again.
   * @param {string} newProperty - Column key that was clicked.
   * **Called by:
   * @see {TransectDataTable} - via each column header's TableSortLabel onClick.
   * @memberof module:SHARED
   * @returns {void}
   */
  const handleSort = (newProperty) => {
    if (newProperty === sortProperty) {
      setOrdering((ordering) => (ordering === "asc" ? "desc" : "asc"));
    } else {
      setOrdering("asc");
      setSortProperty(newProperty);
    }
  };

  return (
    <div style={{ maxHeight: "55vh", overflow: "auto", marginTop: 8 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.map(({ key, label }) => (
              <TableCell key={key} align="center" style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                <TableSortLabel
                  active={key === sortProperty}
                  direction={ordering}
                  onClick={(e) => handleSort(key)}
                >
                  {t(label)}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows().map((row) => (
            <TableRow key={row.id}>
              {COLUMNS.map(({ key, numeric, format }) => {
                let value = row[key];
                if (key === "label") {
                  const computedLabel = getEstevesLabel(row.lrr);
                  if (computedLabel) value = computedLabel;
                }
                return (
                <TableCell
                  align={numeric ? "left" : "center"}
                  key={key}
                  style={{ padding: "4px 8px", whiteSpace: "nowrap" }}
                >
                  {format
                    ? format(value)
                    : numeric
                    ? (value === null || value === undefined || isNaN(value) || !isFinite(value)
                        ? "N/A"
                        : parseFloat(value).toFixed(3))
                    : value}
                </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default TransectDataTable;
